"use client";

import type { LanguageJourneyChapter } from "@/lib/languageJourney";
import type { SpanishJourneyPlan } from "@/lib/spanishJourneyPlanner";
import { Lock } from "lucide-react";

function SpanishJourneyChapterSummary({
  chapter,
  eyebrow,
  showRecommendationPrompt = false,
}: {
  chapter: LanguageJourneyChapter;
  eyebrow: string;
  showRecommendationPrompt?: boolean;
}) {
  return (
    <div data-testid="spanish-journey-chapter-summary" className="min-w-0">
      <p className="text-[10px] font-black uppercase text-orange-300/70">{eyebrow}</p>
      <h3 className="mt-1 truncate text-lg font-black text-white sm:text-xl">
        Chapter {chapter.chapterNumber}: {chapter.title}
      </h3>
      {showRecommendationPrompt && (
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/45">
          Ready for your next Journey session. You can choose another chapter or ask Chicky to build
          an eight-session recommendation.
        </p>
      )}
    </div>
  );
}

export function SpanishJourneyOverviewCard({
  plan,
  recommendedChapter,
  selectedChapter,
  hasExplicitChapterSelection = false,
  completedPlanItems,
  recommendedChapterUnlocked,
  onOpenPlanner,
  onOpenPath,
  onUpgrade,
}: {
  plan: SpanishJourneyPlan | null;
  recommendedChapter: LanguageJourneyChapter | null;
  selectedChapter: LanguageJourneyChapter | null;
  hasExplicitChapterSelection?: boolean;
  completedPlanItems: number;
  recommendedChapterUnlocked: boolean;
  onOpenPlanner: () => void;
  onOpenPath: () => void;
  onUpgrade: () => void;
}) {
  const chapter = recommendedChapter;
  const planComplete = Boolean(plan && completedPlanItems === plan.items.length);
  const isDraft = plan?.status === "draft";

  return (
    <div className="mb-5 overflow-hidden rounded-lg border border-white/10 bg-[#121318] text-left shadow-2xl shadow-black/30">
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1.45fr)_minmax(17rem,0.55fr)]">
        <div className="p-4 sm:p-5">
          <div className="text-[10px] font-black uppercase text-orange-300/75">
            Spanish Language Journey
          </div>

          {plan ? (
            <>
              {chapter ? (
                <div className="mt-2">
                  <SpanishJourneyChapterSummary
                    chapter={chapter}
                    eyebrow={
                      recommendedChapterUnlocked ? "Chicky suggests next" : "Suggested next · Pro"
                    }
                  />
                </div>
              ) : planComplete ? (
                <div className="mt-2">
                  <p className="text-[10px] font-black uppercase text-emerald-300/70">
                    Plan complete
                  </p>
                  <p className="mt-1 text-sm font-bold text-white sm:text-base">
                    Continue through the course or build your next route.
                  </p>
                </div>
              ) : selectedChapter ? (
                <p className="text-xs font-semibold text-white/40">
                  Open your path to choose the next reviewed session.
                </p>
              ) : null}
            </>
          ) : hasExplicitChapterSelection && selectedChapter ? (
            <div className="mt-2">
              <SpanishJourneyChapterSummary
                chapter={selectedChapter}
                eyebrow="Selected chapter"
                showRecommendationPrompt
              />
            </div>
          ) : (
            <>
              <h3 className="mt-2 text-lg font-black text-white sm:text-xl">
                Turn the course into your path
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/45">
                Choose your goal and exact starting chapter. Chicky will highlight a short route
                inside the complete reviewed Spanish course.
              </p>
            </>
          )}
        </div>

        <div className="flex flex-col justify-center gap-2 border-t border-white/10 bg-black/15 p-4 lg:border-l lg:border-t-0">
          <button
            type="button"
            onClick={
              plan && !isDraft && chapter && !recommendedChapterUnlocked
                ? onUpgrade
                : plan && !isDraft
                  ? onOpenPath
                  : onOpenPlanner
            }
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-orange-500 px-5 text-sm font-black text-white transition-colors hover:bg-orange-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60"
          >
            {plan && !isDraft && chapter && !recommendedChapterUnlocked ? (
              <>
                <Lock className="h-4 w-4" aria-hidden="true" />
                Unlock next session
              </>
            ) : plan && !isDraft ? (
              "Open full course"
            ) : plan ? (
              "Review my plan"
            ) : (
              "Build my plan"
            )}
          </button>
          <button
            type="button"
            onClick={plan ? onOpenPlanner : onOpenPath}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.025] px-4 text-xs font-bold text-white/60 transition-colors hover:bg-white/[0.055] hover:text-white"
          >
            {plan ? "Adjust recommendation" : "Browse the full course"}
          </button>
        </div>
      </div>
    </div>
  );
}
